import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faHourglassHalf,
  faPauseCircle,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import GetUser from "../utils/getUser";
import UseGraph from "../utils/useGraph";
import formQueryUrl from "../utils/formQueryUrl";

type WidgetItem = {
  status: string;
  label: string;
  icon: IconDefinition;
  iconClass: string;
  ringClass: string;
};

const widgetData: WidgetItem[] = [
  {
    status: "Awaiting-Customer",
    label: "Awaiting",
    icon: faHourglassHalf,
    iconClass: "text-amber-400",
    ringClass: "bg-amber-500/10 border-amber-500/25",
  },
  {
    status: "On-Hold",
    label: "On Hold",
    icon: faPauseCircle,
    iconClass: "text-indigo-500",
    ringClass: "bg-indigo-500/10 border-indigo-500/25",
  },
  {
    status: "Debt-Collectors",
    label: "Collectors",
    icon: faCircleExclamation,
    iconClass: "text-red-400",
    ringClass: "bg-red-500/10 border-red-500/25",
  },
  {
    status: "Collected",
    label: "Collected",
    icon: faCircleCheck,
    iconClass: "text-emerald-400",
    ringClass: "bg-emerald-500/10 border-emerald-500/25",
  },
  {
    status: "Failed",
    label: "Failed",
    icon: faCircleXmark,
    iconClass: "text-amber-400",
    ringClass: "bg-amber-500/10 border-amber-500/25",
  },
  {
    status: "Chargebacks",
    label: "Chargebacks",
    icon: faBan,
    iconClass: "text-red-400",
    ringClass: "bg-red-500/10 border-red-500/25",
  },
];

export default function TopBarWidget() {
  const user = GetUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [widgetCount, setWidgetCount] = useState<Record<string, any>[]>([]);
  const isMyCustomer = window.location.pathname === "/my-customers";
  const { startDate, endDate, isRestarted } = UseGraph();

  useEffect(() => {
    const findWidgets = async () => {
      setLoading(true);
      const filtersArray = [
        isRestarted && { restarted: true },
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const widgetData = await axiosInstance.get(
          `/stats/chase/counts${formQueryUrl(filtersArray, "?")}`
        );
        setWidgetCount(widgetData.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    findWidgets();
  }, [user?.code, isMyCustomer, startDate, endDate, isRestarted]);

  return (
    <div className="mt-8 grid grid-cols-6 max-lg:grid-cols-3 max-sm:grid-cols-2 gap-4 max-sm:gap-3">
      {widgetData.map((item) => {
        const actionedData = ["Cancelled", "Rebroked", "Mismatched"];
        const currentWidegtDetails =
          (item?.status as string) === "Actioned"
            ? {
                count: widgetCount.reduce((prevWC: any, newWC: any) => {
                  return (prevWC += actionedData?.includes(newWC?.status)
                    ? Number(newWC?.count)
                    : 0);
                }, 0),
              }
            : widgetCount.find((widgetC) => widgetC.status === item.status);

        if (loading) {
          return (
            <div
              className="animate-pulse w-[100%] h-[85px] bg-surface border border-edge rounded-xl"
              key={item.status}
            ></div>
          );
        }

        return (
          <div
            className="bg-surface border border-edge w-[100%] rounded-xl shadow-card hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300 h-auto"
            key={item.status}
          >
            <div className="flex p-4 gap-3 font-medium items-center">
              <div
                className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${item.ringClass}`}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={`text-[18px] ${item.iconClass}`}
                />
              </div>
              <div className="flex flex-col">
                <p className="font-medium leading-5 text-[16px] text-light-gray text-nowrap !capitalize">
                  {item.label}
                </p>

                <p className="bg-gradient-heading bg-clip-text text-transparent font-bold text-[22px]">
                  {currentWidegtDetails?.count
                    ? currentWidegtDetails?.count
                    : 0}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
